import React from "react";
import Select from "react-select";
import Flatpickr from "react-flatpickr";
import Korean from "flatpickr/dist/l10n/ko.js";
import "flatpickr/dist/flatpickr.css";
import { Building2, CalendarDays, Calculator, GraduationCap, LoaderCircle, Send, Share2, Users, X } from "lucide-react";
import { Button, Input, Label, Modal } from "@/Components/Common/Tailwind";
import QuantityButtonInput from "@/Components/Common/QuantityButtonInput";
import {
  GroupReservationAgencyContext,
  GroupReservationOptionProduct,
  GroupReservationPublicConfig,
} from "@/api/types/group-reservation";
import {
  buildPreferredTimeOptions,
  calculateGroupReservationOptionEstimate,
  formatPhoneNumber,
} from "../groupReservationForm.mjs";

type GroupReservationFormState = {
  group_type: string;
  visit_start_date: string;
  visit_end_date: string;
  preferred_time: string;
  group_name: string;
  headcount: string;
  adult_count: string;
  child_count: string;
  leader_count: string;
  preferred_package_id: string;
  option_product_ids: number[];
  option_product_quantities: Record<number, string>;
  request_memo: string;
  applicant_name: string;
  applicant_phone: string;
  applicant_email: string;
  privacy_agreed: boolean;
};

type GroupReservationFormProps = {
  form: GroupReservationFormState;
  errors: Record<string, string>;
  config: GroupReservationPublicConfig;
  agencyContext?: GroupReservationAgencyContext | null;
  submitting: boolean;
  onChange: (
    field: keyof GroupReservationFormState,
    value: string | boolean | number[] | Record<number, string>
  ) => void;
  onSubmit: (event: React.FormEvent<HTMLFormElement>) => void;
  onShareLink: () => void;
};

const FieldError = ({ message }: { message?: string }) => {
  if (!message) return null;
  return <p className="mt-1 text-xs font-semibold text-red-600">{message}</p>;
};

const SectionHeader = ({
  icon,
  title,
  description,
}: {
  icon: React.ReactNode;
  title: string;
  description?: string;
}) => (
  <div className="mb-4 flex items-start gap-3">
    <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-emerald-600 text-white">
      {icon}
    </div>
    <div>
      <h2 className="text-base font-black text-gray-900 text-balance">{title}</h2>
      {description ? <p className="mt-1 text-sm leading-5 text-gray-600 text-pretty">{description}</p> : null}
    </div>
  </div>
);

const FormField = ({
  label,
  error,
  children,
}: {
  label: string;
  error?: string;
  children: React.ReactNode;
}) => (
  <div>
    <Label className="text-sm font-bold text-gray-800">{label}</Label>
    {children}
    <FieldError message={error} />
  </div>
);

const formatAmount = (value: number) => `${Number(value || 0).toLocaleString()}원`;

const WEEKDAY_LABELS = ["일", "월", "화", "수", "목", "금", "토"];
const formatDisplayDate = (value: string) => {
  if (!value) return "";
  const [year, month, day] = value.split("-").map(Number);
  if (!year || !month || !day) return value;
  const weekday = WEEKDAY_LABELS[new Date(year, month - 1, day).getDay()];
  return `${year}년 ${month}월 ${day}일 (${weekday})`;
};

type TimeOption = { value: string; label: string };

const EMERALD = "#059669";

// react-select을 폼의 emerald 톤에 맞춘다 (선택 강조/hover/focus를 시스템 파랑 대신 emerald로).
const selectTheme = (theme: any) => ({
  ...theme,
  colors: {
    ...theme.colors,
    primary: EMERALD, // 선택된 옵션 배경 + focus 테두리
    primary25: "#ecfdf5", // hover 옵션 배경
    primary50: "#d1fae5", // active 옵션 배경
    neutral30: "#d1d5db", // hover 시 테두리
  },
});

const selectStyles = {
  control: (base: any, state: any) => ({
    ...base,
    minHeight: 44,
    borderRadius: 8,
    borderColor: state.isFocused ? EMERALD : "#d1d5db",
    boxShadow: state.isFocused ? "0 0 0 2px rgba(5,150,105,0.2)" : "none",
    "&:hover": { borderColor: EMERALD },
  }),
  menu: (base: any) => ({ ...base, zIndex: 30, borderRadius: 12, overflow: "hidden" }),
  menuList: (base: any) => ({ ...base, padding: 4 }),
  option: (base: any) => ({ ...base, borderRadius: 8, cursor: "pointer" }),
  placeholder: (base: any) => ({ ...base, color: "#9ca3af" }),
};

const GROUP_TYPE_OPTIONS = [
  { value: "general", label: "일반", icon: Building2 },
  { value: "academy", label: "학원", icon: GraduationCap },
] as const;

const GroupTypeToggle = ({
  value,
  onChange,
}: {
  value: string;
  onChange: (value: string) => void;
}) => (
  <div className="grid grid-cols-2 gap-2 rounded-2xl bg-gray-100 p-1">
    {GROUP_TYPE_OPTIONS.map((option) => {
      const selected = value === option.value;
      const OptionIcon = option.icon;
      return (
        <button
          key={option.value}
          type="button"
          aria-pressed={selected}
          onClick={() => onChange(option.value)}
          className={`flex h-11 items-center justify-center gap-2 rounded-xl text-sm font-bold transition-all active:scale-[0.98] ${
            selected
              ? "bg-white text-emerald-700 shadow-[0_1px_3px_rgba(0,0,0,0.12)]"
              : "text-gray-500 hover:text-gray-700"
          }`}
        >
          <OptionIcon size={16} />
          {option.label}
        </button>
      );
    })}
  </div>
);

// 옵션 수량 기본값은 인원수와 무관하게 항상 1 (인원수를 자동으로 채우지 않음)
const getDefaultOptionQuantity = (_form: GroupReservationFormState) => "1";

const OptionProductSelector = ({
  form,
  optionProducts,
  onChange,
}: {
  form: GroupReservationFormState;
  optionProducts: GroupReservationOptionProduct[];
  onChange: (ids: number[], quantities: Record<number, string>) => void;
}) => {
  if (!optionProducts.length) return null;

  const selectedIds = new Set((form.option_product_ids || []).map(Number));
  const quantities = form.option_product_quantities || {};
  const estimate = calculateGroupReservationOptionEstimate(form, optionProducts);
  const subtotalById = new Map<number, number>(estimate.items.map((item) => [item.id, item.totalAmount]));
  const toggleOption = (optionId: number, checked: boolean) => {
    const nextIds = new Set(selectedIds);
    const nextQuantities = { ...quantities };
    if (checked) {
      nextIds.add(optionId);
      if (!nextQuantities[optionId]) {
        nextQuantities[optionId] = getDefaultOptionQuantity(form);
      }
    } else {
      nextIds.delete(optionId);
      delete nextQuantities[optionId];
    }
    onChange(Array.from(nextIds), nextQuantities);
  };
  const updateQuantity = (optionId: number, quantity: string) => {
    onChange(form.option_product_ids || [], {
      ...quantities,
      [optionId]: quantity,
    });
  };

  return (
    <div className="rounded-2xl bg-white px-4 py-4 shadow-[0_1px_2px_rgba(0,0,0,0.04),0_2px_8px_rgba(0,0,0,0.06)]">
      <SectionHeader
        icon={<Calculator size={18} />}
        title="단체예약 옵션"
        description="선택한 옵션은 신청 예상 금액에 반영됩니다."
      />
      <div className="space-y-3">
        {optionProducts.map((optionProduct) => {
          const selected = selectedIds.has(optionProduct.id);
          const checkboxId = `group-option-${optionProduct.id}`;
          return (
            <div
              key={optionProduct.id}
              className={`rounded-xl border px-3 py-3 transition-colors ${
                selected ? "border-emerald-300 bg-emerald-50/40" : "border-gray-200"
              }`}
            >
              <div className="flex items-start gap-3">
                <Input
                  id={checkboxId}
                  type="checkbox"
                  checked={selected}
                  onChange={(event) => toggleOption(optionProduct.id, event.target.checked)}
                  className="mt-0.5 h-[15px] w-[15px] accent-emerald-600"
                />
                <label htmlFor={checkboxId} className="min-w-0 flex-1 cursor-pointer">
                  <span className="block text-sm font-black text-gray-900 text-pretty">{optionProduct.name}</span>
                  {optionProduct.description ? (
                    <span className="mt-1 block text-xs leading-5 text-gray-500 text-pretty">{optionProduct.description}</span>
                  ) : null}
                  <span className="mt-2 block text-xs font-semibold text-gray-500 tabular-nums">
                    주중 <span className="font-bold text-gray-900">{formatAmount(optionProduct.weekday_price)}</span>
                    {" / "}
                    주말 <span className="font-bold text-gray-900">{formatAmount(optionProduct.weekend_price)}</span>
                  </span>
                </label>
              </div>
              {selected ? (
                <div className="mt-3 flex items-center gap-3 pl-8">
                  <span className="text-xs font-bold text-gray-700">수량</span>
                  <QuantityButtonInput
                    variant="subtle"
                    minQuantity={1}
                    quantity={Number(quantities[optionProduct.id] ?? getDefaultOptionQuantity(form)) || 1}
                    onChange={(value) => updateQuantity(optionProduct.id, String(value))}
                  />
                  {subtotalById.has(optionProduct.id) ? (
                    <span className="ml-auto text-sm font-black text-emerald-600 tabular-nums">
                      소계 {formatAmount(subtotalById.get(optionProduct.id) as number)}
                    </span>
                  ) : null}
                </div>
              ) : null}
            </div>
          );
        })}
      </div>
      <div className="mt-4 rounded-xl bg-gray-50 px-3 py-3">
        <div className="flex items-center justify-between gap-3">
          <span className="text-sm font-bold text-gray-700">예상 옵션 합계</span>
          <span className="text-base font-black text-gray-900 tabular-nums">{formatAmount(estimate.totalAmount)}</span>
        </div>
        {estimate.items.length ? (
          <div className="mt-2 space-y-1">
            {estimate.items.map((item) => (
              <div key={item.id} className="flex justify-between gap-3 text-xs text-gray-600">
                <span className="tabular-nums">{item.name} x {item.quantity}개</span>
                <span className="font-semibold tabular-nums">{formatAmount(item.totalAmount)}</span>
              </div>
            ))}
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default function GroupReservationForm({
  form,
  errors,
  config,
  agencyContext,
  submitting,
  onChange,
  onSubmit,
  onShareLink,
}: GroupReservationFormProps) {
  const preferredTimeOptions = buildPreferredTimeOptions(form, config);
  const isAcademy = form.group_type === "academy";
  const optionProducts = config.option_products || [];
  const estimate = calculateGroupReservationOptionEstimate(form, optionProducts);
  const timeOptions: TimeOption[] = preferredTimeOptions.map((option) => ({ value: option, label: option }));
  const selectedTimeOption = form.preferred_time
    ? timeOptions.find((option) => option.value === form.preferred_time) ?? null
    : null;
  const [dateModalOpen, setDateModalOpen] = React.useState(false);

  const handlePhoneChange = (rawValue: string) => {
    onChange("applicant_phone", formatPhoneNumber(rawValue));
  };

  return (
    <form onSubmit={onSubmit} className="space-y-7 px-5 py-6 pb-28 break-keep break-words">
      <div className="rounded-2xl bg-white px-5 py-5 shadow-[0_1px_2px_rgba(0,0,0,0.04),0_2px_8px_rgba(0,0,0,0.06)]">
        <p className="text-xs font-bold uppercase tracking-wide text-emerald-700">
          {agencyContext ? "대리점 단체 예약 신청" : "단체 예약 신청"}
        </p>
        <h1 className="mt-1 text-2xl font-black leading-tight text-gray-900 text-balance">방문 일정과 인원을 남겨주세요</h1>
        <p className="mt-2 text-sm leading-6 text-gray-600 text-pretty">
          담당자가 신청 내용을 확인한 뒤 실제 예약 일정과 계좌이체 안내를 연락드립니다.
        </p>
        {agencyContext ? (
          <div className="mt-4 rounded-xl bg-emerald-50 px-3 py-2 text-sm font-semibold text-emerald-800">
            담당 대리점: {agencyContext.agency_label}
          </div>
        ) : null}
      </div>

      <section>
        <SectionHeader
          icon={<CalendarDays size={18} />}
          title="방문 일정"
          description="방문을 희망하는 날짜를 선택해주세요."
        />
        <FormField label="희망 방문일" error={errors.visit_start_date}>
          <button
            type="button"
            id="group-field-visit_start_date"
            onClick={() => setDateModalOpen(true)}
            className="flex h-11 w-full items-center justify-between rounded-lg border border-gray-300 bg-white px-3 text-base text-left transition-colors hover:border-emerald-600 focus:border-emerald-600 focus:outline-none focus:ring-2 focus:ring-emerald-600/20"
          >
            <span className={form.visit_start_date ? "text-gray-900" : "text-gray-500"}>
              {form.visit_start_date ? formatDisplayDate(form.visit_start_date) : "방문일을 선택하세요"}
            </span>
            <CalendarDays size={18} className="shrink-0 text-gray-500" />
          </button>
        </FormField>
        <div className="mt-4">
          <FormField label="희망 시간대">
            <Select<TimeOption>
              inputId="group-field-preferred_time"
              classNamePrefix="gr-select"
              options={timeOptions}
              value={selectedTimeOption}
              onChange={(option) => onChange("preferred_time", option ? option.value : "")}
              placeholder="시간대 선택"
              isSearchable={false}
              noOptionsMessage={() => "선택 가능한 시간대가 없습니다"}
              theme={selectTheme}
              styles={selectStyles}
            />
          </FormField>
        </div>
      </section>

      <section className="border-t border-gray-100 pt-7">
        <SectionHeader icon={<Users size={18} />} title="단체 정보" />
        {config.rule_message ? (
          <div className="mb-4 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm font-semibold leading-6 text-amber-900 text-pretty">
            {config.rule_message}
          </div>
        ) : null}
        <div className="space-y-4">
          <FormField label="단체 타입">
            <GroupTypeToggle
              value={form.group_type}
              onChange={(value) => onChange("group_type", value)}
            />
          </FormField>
          <FormField label="단체명/기관명" error={errors.group_name}>
            <Input
              id="group-field-group_name"
              value={form.group_name}
              onChange={(event) => onChange("group_name", event.target.value)}
              placeholder="예: 서울초등학교, 어메이징 주식회사"
              className="h-11"
            />
          </FormField>

          {!isAcademy ? (
            <FormField label="인원 수" error={errors.headcount}>
              <Input
                id="group-field-headcount"
                type="number"
                min="2"
                inputMode="numeric"
                value={form.headcount}
                onChange={(event) => onChange("headcount", event.target.value)}
                placeholder="2명 이상"
                className="h-11 tabular-nums"
              />
            </FormField>
          ) : (
            <div className="grid grid-cols-2 gap-3">
              <FormField label="총 인원" error={errors.headcount}>
                <Input
                  id="group-field-headcount"
                  type="number"
                  min="2"
                  inputMode="numeric"
                  value={form.headcount}
                  onChange={(event) => onChange("headcount", event.target.value)}
                  placeholder="2명 이상"
                  className="h-11 tabular-nums"
                />
              </FormField>
              <FormField label="인솔자 수">
                <Input
                  type="number"
                  min="0"
                  inputMode="numeric"
                  value={form.leader_count}
                  onChange={(event) => onChange("leader_count", event.target.value)}
                  placeholder="선택"
                  className="h-11 tabular-nums"
                />
              </FormField>
              <FormField label="성인 수">
                <Input
                  type="number"
                  min="0"
                  inputMode="numeric"
                  value={form.adult_count}
                  onChange={(event) => onChange("adult_count", event.target.value)}
                  placeholder="선택"
                  className="h-11 tabular-nums"
                />
              </FormField>
              <FormField label="아동 수">
                <Input
                  type="number"
                  min="0"
                  inputMode="numeric"
                  value={form.child_count}
                  onChange={(event) => onChange("child_count", event.target.value)}
                  placeholder="선택"
                  className="h-11 tabular-nums"
                />
              </FormField>
            </div>
          )}

          <OptionProductSelector
            form={form}
            optionProducts={config.option_products || []}
            onChange={(ids, quantities) => {
              onChange("option_product_ids", ids);
              onChange("option_product_quantities", quantities);
            }}
          />

          <FormField label="요청사항">
            <Input
              type="textarea"
              rows={4}
              value={form.request_memo}
              onChange={(event) => onChange("request_memo", event.target.value)}
              placeholder="차량, 식사, 휠체어, 기타 문의사항을 입력해주세요."
            />
          </FormField>
        </div>
      </section>

      <section className="border-t border-gray-100 pt-7">
        <SectionHeader icon={<Users size={18} />} title="신청자 정보" />
        <div className="space-y-4">
          <FormField label="신청자명" error={errors.applicant_name}>
            <Input
              id="group-field-applicant_name"
              value={form.applicant_name}
              onChange={(event) => onChange("applicant_name", event.target.value)}
              placeholder="담당자 이름"
              className="h-11"
            />
          </FormField>
          <FormField label="연락처" error={errors.applicant_phone}>
            <Input
              id="group-field-applicant_phone"
              type="tel"
              inputMode="numeric"
              maxLength={13}
              value={form.applicant_phone}
              onChange={(event) => handlePhoneChange(event.target.value)}
              placeholder="010-0000-0000"
              className="h-11 tabular-nums"
            />
          </FormField>
          <FormField label="이메일">
            <Input
              type="email"
              value={form.applicant_email}
              onChange={(event) => onChange("applicant_email", event.target.value)}
              placeholder="선택사항"
              className="h-11"
            />
          </FormField>
        </div>
      </section>

      <section className="border-t border-gray-100 pt-7">
        <div className="rounded-2xl bg-gray-50 px-4 py-4">
          <div className="flex items-start justify-between gap-3">
            <div>
              <h2 className="text-base font-black text-gray-900 text-balance">계좌이체 안내</h2>
              <p className="mt-2 text-sm leading-6 text-gray-600 text-pretty">
                최종 예약 확정과 입금 안내는 담당자 확인 후 계좌이체로 진행됩니다.
              </p>
              {config.bank_notice ? (
                <p className="mt-2 text-sm leading-6 text-gray-700 text-pretty">{config.bank_notice}</p>
              ) : null}
            </div>
            <Button
              type="button"
              variant="outline"
              size="icon"
              onClick={onShareLink}
              aria-label="신청 링크 공유"
              className="transition-transform active:scale-[0.96]"
            >
              <Share2 size={18} />
            </Button>
          </div>
        </div>

        <label className="mt-4 flex items-start gap-3 rounded-2xl border border-gray-200 px-4 py-3 transition-colors has-[:checked]:border-emerald-300 has-[:checked]:bg-emerald-50/40">
          <Input
            id="group-field-privacy_agreed"
            type="checkbox"
            checked={form.privacy_agreed}
            onChange={(event) => onChange("privacy_agreed", event.target.checked)}
            className="mt-0.5 h-5 w-5"
          />
          <span className="text-sm leading-6 text-gray-700 text-pretty">
            단체 예약 상담과 연락을 위해 신청자 정보를 수집 및 이용하는 데 동의합니다.
          </span>
        </label>
        <FieldError message={errors.privacy_agreed} />
      </section>

      <div className="fixed bottom-0 left-1/2 z-20 w-full max-w-160 -translate-x-1/2 border-t border-gray-200 bg-white/80 backdrop-blur-md">
        <div className="px-7 py-3">
          {/* 신청 버튼 */}
          <Button
            type="submit"
            className="h-12 w-full border-emerald-600 bg-emerald-600 text-white transition-transform hover:bg-emerald-700 focus-visible:ring-emerald-600/30 active:scale-[0.98]"
            disabled={submitting}
          >
            {submitting ? <LoaderCircle size={18} className="animate-spin" /> : <Send size={18} />}
            {submitting ? "접수 중" : "단체 예약 신청하기"}
          </Button>
        </div>
      </div>

      <Modal
        isOpen={dateModalOpen}
        toggle={() => setDateModalOpen(false)}
        variant="sheet"
        title="방문일 선택"
        overlayClassName="backdrop-blur-none bg-black/40"
      >
        <div className="px-5 pb-6 pt-5">
          <div className="relative mb-4 flex h-8 items-center justify-center">
            <h2 className="text-base font-black text-gray-900">일정 선택</h2>
            <button
              type="button"
              onClick={() => setDateModalOpen(false)}
              aria-label="닫기"
              className="absolute right-0 top-1/2 flex h-8 w-8 -translate-y-1/2 items-center justify-center rounded-full text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-700"
            >
              <X size={20} />
            </button>
          </div>
          <div className="gr-datepicker">
            <Flatpickr
              value={form.visit_start_date || undefined}
              options={{
                inline: true,
                mode: "single",
                dateFormat: "Y-m-d",
                minDate: "today",
                disableMobile: true,
                locale: Korean.ko,
                defaultDate: form.visit_start_date || "today",
              }}
              onChange={(_selectedDates: Date[], dateStr: string) => {
                if (dateStr) {
                  onChange("visit_start_date", dateStr);
                  setDateModalOpen(false);
                }
              }}
            />
          </div>
        </div>
      </Modal>
    </form>
  );
}
